import { createFileRoute } from "@tanstack/react-router";
import { LegalLayout } from "@/components/LegalLayout";
import { useLocale } from "@/contexts/LocaleContext";
import { DICTS } from "@/lib/i18n";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";
import { ADDED_SHIPPING_COUNTRIES, INTERNATIONAL_SHIPPING_POLICY } from "@/lib/international-shipping";
import { MARKETS } from "@/lib/markets";

import { publicCacheHeaders } from "@/lib/public-cache-headers";

export const Route = createFileRoute("/shipping")({
  headers: publicCacheHeaders,
  head: () => ({
    meta: [
      { title: DICTS.en["legal.shipping.meta.title"] },
      { name: "description", content: DICTS.en["legal.shipping.meta.description"] },
      { property: "og:title", content: DICTS.en["legal.shipping.meta.title"] },
      { property: "og:description", content: DICTS.en["legal.shipping.meta.description"] },
      { property: "og:url", content: `${PUBLIC_SITE_ORIGIN}/shipping` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${PUBLIC_SITE_ORIGIN}/shipping` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Lecmo",
          url: PUBLIC_SITE_ORIGIN,
          hasShippingService: {
            "@type": "ShippingService",
            "@id": `${PUBLIC_SITE_ORIGIN}/shipping#uae-shipping`,
            name: "UAE delivery",
            description: `${DICTS.en["legal.shipping.service_intro"]} ${DICTS.en["legal.shipping.same_day_detail"]}`,
            shippingConditions: {
              "@type": "ShippingConditions",
              shippingDestination: {
                "@type": "DefinedRegion",
                addressCountry: "AE",
              },
              shippingRate: {
                "@type": "MonetaryAmount",
                value: 0,
                currency: "AED",
              },
            },
          },
        }),
      },
    ],
  }),
  component: ShippingPage,
});

function ShippingPage() {
  const { t, locale } = useLocale();
  const shippingVars = {
    fee: String(INTERNATIONAL_SHIPPING_POLICY.standard),
    threshold: String(INTERNATIONAL_SHIPPING_POLICY.freeSubtotal),
    minDays: String(INTERNATIONAL_SHIPPING_POLICY.minBusinessDays),
    maxDays: String(INTERNATIONAL_SHIPPING_POLICY.maxBusinessDays),
  };

  const supportedCountries: Array<{ flag: string; key: string; time: string; fee: string }> = [
    { flag: "🇦🇪", key: "legal.shipping.country_ae", time: "legal.shipping.same_day", fee: "legal.shipping.free" },
    { flag: "🇸🇦", key: "legal.shipping.country_sa", time: "legal.shipping.sa_time", fee: "legal.shipping.sa_fee" },
    { flag: "🇴🇲", key: "legal.shipping.country_om", time: "legal.shipping.same_day", fee: "legal.shipping.free" },
    { flag: "🇶🇦", key: "legal.shipping.country_qa", time: "legal.shipping.same_day", fee: "legal.shipping.free" },
    { flag: "🇮🇶", key: "legal.shipping.country_iq", time: "legal.shipping.iq_time", fee: "legal.shipping.iraq_fee" },
  ];

  return (
    <LegalLayout titleKey="legal.shipping.title" eyebrowKey="legal.shipping.eyebrow">
      <p>{t("legal.shipping.intro")}</p>
      <p>{t("legal.shipping.service_intro")}</p>


      <h2>{t("legal.shipping.h2_supported")}</h2>
      <div className="not-prose overflow-hidden border border-border">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30 text-foreground">
              <th className="px-4 py-3 text-start font-medium">
                {t("legal.shipping.col_country")}
              </th>
              <th className="px-4 py-3 text-start font-medium">
                {t("legal.shipping.col_time")}
              </th>
              <th className="px-4 py-3 text-start font-medium">
                {t("legal.shipping.col_note")}
              </th>
            </tr>
          </thead>
          <tbody>
            {supportedCountries.map((c) => (
              <tr key={c.key} className="border-b border-border last:border-0">
                <td className="px-4 py-3 text-foreground">
                  <span className="me-2">{c.flag}</span>
                  {t(c.key)}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {t(c.time)}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {t(c.fee)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>{t("legal.shipping.kw_bh_title")}</h2>
      <p>{t("legal.shipping.kw_bh_rates")}</p>

      <h2>{t("legal.shipping.country_us")}</h2>
      <p>{t("legal.shipping.us_orders")}</p>
      <p>{t("legal.shipping.us_timing")}</p>
      <p>{t("legal.shipping.us_charges")}</p>

      <h2>{t("legal.shipping.added_title")}</h2>
      <p>{t("legal.shipping.added_rates", shippingVars)}</p>
      <p>{t("legal.shipping.added_timing", shippingVars)}</p>
      <p>{t("legal.shipping.added_countries")}</p>
      <p>{ADDED_SHIPPING_COUNTRIES.map((code) => MARKETS[code].labels[locale]).join(" · ")}</p>
      <p>{t("legal.shipping.added_more")}</p>
      <p>{t("legal.shipping.us_charges")}</p>

      <h2>{t("legal.shipping.h2_rest")}</h2>
      <div className="not-prose overflow-hidden border border-border">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30 text-foreground">
              <th className="px-4 py-3 text-start font-medium">
                {t("legal.shipping.col_region")}
              </th>
              <th className="px-4 py-3 text-start font-medium">
                {t("legal.shipping.col_time")}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-4 py-3 text-foreground">
                <span className="me-2">🌍</span>
                {t("legal.shipping.rest_world")}
              </td>
              <td className="px-4 py-3 text-muted-foreground">
                {t("legal.shipping.within_week")}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>{t("legal.shipping.international_fee")}</p>
      <p className="text-sm text-muted-foreground">{t("legal.shipping.note")}</p>
    </LegalLayout>
  );
}
